import { useQueryClient } from "@tanstack/react-query";
import { formatAddress, toTitleCase } from "@utils/strings";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Button, Heading, Label, Paragraph, Select, type SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  type ApproveClinicListingRequestInput,
  BasecampClinicListingsRetrieveVerificationStatus,
  type CreateOrganizationInput,
  type GetClinicListingsOutput,
  type GetOrganizationsOutput,
  getBasecampClinicListingsRetrieveQueryKey,
  useBasecampClinicListingsApproveCreate,
  useBasecampClinicListingsRetrieve,
  useBasecampOrganizationsRetrieve,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { OrganizationFormFields } from "@components/forms/OrganizationFormFields";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ORG_CONNECTION_TYPES } from "@pages/Organizations/constants";

const CREATE_NEW_OPTION: SelectItem = { id: "create-new", primary: "Create New" };

export const VerificationRequestDetailPage: React.FC = () => {
  const { clinicListingId } = useParams<{ clinicListingId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useBasecampClinicListingsRetrieve({
    id: clinicListingId,
    verification_status: BasecampClinicListingsRetrieveVerificationStatus.PENDING,
  });

  const listings: GetClinicListingsOutput[] =
    data?.status === 200 ? (Array.isArray(data.data) ? data.data : [data.data]) : [];
  const clinic = listings[0];

  const { data: orgsData } = useBasecampOrganizationsRetrieve({});
  const { mutateAsync: approveListing } = useBasecampClinicListingsApproveCreate();
  const queryClient = useQueryClient();

  // map the full list of organizations for the drop down
  const orgOptions: SelectItem[] = useMemo(() => {
    const orgs: GetOrganizationsOutput[] =
      orgsData?.status === 200 ? (Array.isArray(orgsData.data) ? orgsData.data : [orgsData.data]) : [];
    return [CREATE_NEW_OPTION, ...orgs.map((o) => ({ id: o.id, primary: o.name ?? "" }))];
  }, [orgsData]);

  const [userSelectedOrg, setUserSelectedOrg] = useState<SelectItem | undefined>(undefined);

  const selectedOrg =
    userSelectedOrg ??
    (clinic?.claimed_by
      ? { id: clinic.claimed_by.organization.id, primary: clinic.claimed_by.organization.name }
      : CREATE_NEW_OPTION);

  const formUtils = useFormUtils<CreateOrganizationInput>({
    defaultValues: {
      name: "",
      display_name: "",
      organization_type: undefined,
      hubspot_company_id: "",
      logo_url: "",
      primary_color: "",
      background_color: "",
      connection_types: ORG_CONNECTION_TYPES,
    },
    mutationFn: async () => {},
  });
  const { trigger, getValues } = formUtils;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    if (!clinicListingId) return;

    const isCreateNew = selectedOrg?.id === CREATE_NEW_OPTION.id;

    if (isCreateNew) {
      const valid = await trigger();
      if (!valid) return;
    }

    setIsSubmitting(true);
    try {
      const data: ApproveClinicListingRequestInput = {
        clinic_listing_id: clinicListingId,
        ...(isCreateNew ? getValues() : { org_id: selectedOrg?.id }),
      };

      const response = await approveListing({ data });

      if (response.status !== 204) {
        notifyError(response.data.detail || "Failed to approve verification request.");
        return;
      }

      notifySuccess("Verification request approved");
      queryClient.invalidateQueries({ queryKey: getBasecampClinicListingsRetrieveQueryKey() });
      navigate("/verification-requests");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return null;
  if (!clinic) return <Navigate to="/verification-requests" replace />;

  const address = clinic.location
    ? formatAddress({
        addressLine1: toTitleCase(clinic.location.address_line_1) || undefined,
        addressLine2: toTitleCase(clinic.location.address_line_2 ?? "") || undefined,
        city: toTitleCase(clinic.location.city),
        state: clinic.location.state,
        zip: clinic.location.postal_code,
      })
    : "—";

  const showCreateNewForm = selectedOrg?.id === CREATE_NEW_OPTION.id;

  return (
    <div className="flex flex-col bg-bg-default min-h-full py-8 items-center px-4 sm:px-6 lg:px-8">
      <div className="min-w-[900px] flex flex-col gap-4">
        <Breadcrumb
          crumbs={[{ label: "Verification requests", to: "/verification-requests" }, { label: clinic.name }]}
        />

        <Heading size="medium">{clinic.name}</Heading>

        {/* Card 1 — Request details */}
        <div className="flex flex-col gap-6 p-8 bg-bg-surface border border-border-strong rounded-2xl">
          <div className="flex flex-col">
            <Paragraph displayType="loud" colorType="primary">
              Request details
            </Paragraph>
            <Paragraph size="small" colorType="secondary">
              These details were entered by the client and must be verified.
            </Paragraph>
          </div>

          <div className="flex flex-col gap-4">
            <Paragraph displayType="loud" colorType="primary">
              Clinic information
            </Paragraph>

            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Clinic name
                </Label>
                <Paragraph colorType="primary">{clinic.name ?? "—"}</Paragraph>
              </div>
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Organization name
                </Label>
                <Paragraph colorType="primary">{clinic.claimed_by?.organization.name ?? "—"}</Paragraph>
              </div>
            </div>

            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Contact
              </Label>
              <Paragraph colorType="primary">{clinic.contact_email ?? "—"}</Paragraph>
            </div>

            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Address
              </Label>
              <Paragraph colorType="primary">{address}</Paragraph>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Phone
                </Label>
                <Paragraph colorType="primary">{clinic.phone_number ?? "—"}</Paragraph>
              </div>
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Website
                </Label>
                {clinic.website_url ? (
                  <a
                    href={clinic.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-rio-600 hover:underline truncate max-w-xs"
                  >
                    {clinic.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                ) : (
                  <Paragraph colorType="primary">—</Paragraph>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Card 2 — Assign organization */}
          <div className="flex flex-col gap-6 p-8 bg-bg-surface border border-border-strong rounded-2xl">
            <div className="flex flex-col">
              <Paragraph displayType="loud" colorType="primary">
                Assign organization
              </Paragraph>
              <Paragraph size="small" colorType="secondary">
                Link this clinic to an existing organization or create a new one.
              </Paragraph>
            </div>

            <div className="max-w-md">
              <Select
                title="Assign to organization"
                items={orgOptions}
                placeholderText="Select organization..."
                selected={selectedOrg}
                setSelected={setUserSelectedOrg}
                enableSearch
                readOnly={!!clinic.claimed_by}
              />
            </div>

            {showCreateNewForm && (
              <div className="flex flex-col gap-4 p-4 border border-border-strong rounded-2xl">
                <OrganizationFormFields formUtils={formUtils} />
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-row gap-4 justify-end pt-2">
            <Button
              type="button"
              variant="primary"
              buttonStyle="border"
              text="Cancel"
              onClick={() => navigate("/verification-requests")}
              disabled={isSubmitting}
            />
            <Button
              type="button"
              variant="primary"
              buttonStyle="filled"
              text={isSubmitting ? "Approving..." : "Approve request"}
              disabled={isSubmitting}
              onClick={handleApprove}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
